import { useRef } from 'react'
import {
  type InfiniteData,
  QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions
} from '@tanstack/react-query'

type SuccessData<T> = Extract<T, { data: any }>
type ExtractItems<T> =
  SuccessData<T> extends { data: { items?: (infer I)[] } } ? I : unknown

interface UseFetchObserver<T extends object> {
  queryKey: QueryKey
  queryFn: (
    pageParam: number,
    limit: number,
    signal?: AbortSignal
  ) => Promise<T> | T
  limit: number
  options?: Omit<
    UseInfiniteQueryOptions<
      T, // TQueryFnData
      Error, // TError
      InfiniteData<T, number>, // TData
      QueryKey, // TQueryKey
      number // TPageParam
    >,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >
  observerOptions?: IntersectionObserverInit
}

export const useFetchObserver = <T extends object>({
  queryFn,
  queryKey,
  options,
  observerOptions,
  limit
}: UseFetchObserver<T>) => {
  const response = useInfiniteQuery<
    T, // TQueryFnData
    Error, // TError
    InfiniteData<T>, // TData
    QueryKey, // TQueryKey
    number // TPageParam
  >({
    queryKey,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      queryFn(pageParam as number, limit, signal),
    getNextPageParam: (lastPage: T, allPages) => {
      const itemsOnLastPageLength =
        (lastPage as { data?: { items?: unknown[] } }).data?.items?.length ?? 0

      if (itemsOnLastPageLength < limit) return undefined
      if (itemsOnLastPageLength === 0) return undefined

      return allPages.length
    },
    ...options
  })

  const observer = useRef<IntersectionObserver | null>(null)

  const lastElementRef = (node: HTMLElement | null) => {
    if (response.isLoading) return
    if (observer.current) observer.current.disconnect()

    observer.current = new IntersectionObserver(
      async (entries) => {
        if (
          entries[0].isIntersecting &&
          response.hasNextPage &&
          !response.isFetching
        ) {
          await response.fetchNextPage()
        }
      },
      {
        ...observerOptions
      }
    )

    if (node) observer.current.observe(node)
  }

  const flatResponse = (response.data?.pages.flatMap(
    (page) => (page as { data?: { items?: unknown[] } })?.data?.items
  ) ?? []) as ExtractItems<T>[]

  return {
    flatResponse,
    lastElementRef,
    response
  }
}
